import { useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SiteConfigurationTemplate, TagMapping } from '../../types';
import { useTagMappingStatus } from 'src/features/sites/hooks';
import { AlarmRulesSection } from './sections/AlarmRulesSection';
import { AssetsSection } from './sections/AssetsSection';
import { ControllersSection } from './sections/ControllersSection';
import { WidgetsSection } from './sections/WidgetsSection';
import { SitePreviewSection } from './sections/SitePreviewSection';

// Types

type SectionKey = 'alarmRules' | 'assets' | 'controllers' | 'widgets' | 'sitePreview';

const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'alarmRules', label: 'Alarm Rules' },
  { key: 'assets', label: 'Assets' },
  { key: 'controllers', label: 'Controllers' },
  { key: 'widgets', label: 'Widgets' },
  { key: 'sitePreview', label: 'Site Preview' },
];

// Main component

type OverviewStepProps = {
  template: SiteConfigurationTemplate;
  tagMapping: TagMapping;
};

export const OverviewStep = ({ template, tagMapping }: OverviewStepProps) => {
  const [activeSection, setActiveSection] = useState<SectionKey>('alarmRules');
  const theme = useTheme();

  const { isBlockingUnmapped } = useTagMappingStatus(template, tagMapping);

  const counts = useMemo(() => {
    const alarmIncluded = template.alarmRules.filter((r) =>
      (r.conditions ?? []).every((c) => !isBlockingUnmapped(c.tagIdempotencyKey))
    ).length;

    const assetIncluded = template.assets.length;

    const widgetIncluded = template.widgets.filter((w) => {
      const always = w.widgetType === 'LIFT_STATION' || w.widgetType === 'ASSET_CONTROLLER';
      const tagIds = (w.tags ?? []).map((t) => t.tagIdempotencyKey);
      return always || !tagIds.length || !tagIds.every((id) => isBlockingUnmapped(id));
    }).length;

    return {
      alarmRules: { included: alarmIncluded, total: template.alarmRules.length },
      assets: { included: assetIncluded, total: template.assets.length },
      controllers: { included: template.controllers.length, total: template.controllers.length },
      widgets: { included: widgetIncluded, total: template.widgets.length },
      sitePreview: {
        included: template.sitePreview.tags.filter(
          (tag) => !isBlockingUnmapped(tag.tagIdempotencyKey)
        ).length,
        total: template.sitePreview.tags.length,
      },
    };
  }, [template, isBlockingUnmapped]);

  /** Grand totals across all sections */
  const totalIncluded = Object.values(counts).reduce((sum, c) => sum + c.included, 0);
  const totalExcluded = Object.values(counts).reduce((sum, c) => sum + (c.total - c.included), 0);

  return (
    <Stack spacing={2}>
      {/* Header row: description + global included/excluded summary */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          Review what will be created on import based on your tag mapping.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 2 }}>
          <Chip
            size="small"
            label={`Included: ${totalIncluded}`}
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            size="small"
            label={`Excluded: ${totalExcluded}`}
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </Stack>

      {/* Section toggle buttons — full width, white, evenly spaced */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {SECTIONS.map(({ key, label }) => {
          const { included, total } = counts[key];
          const hasExcluded = total > 0 && included < total;
          const isActive = activeSection === key;
          return (
            <Button
              key={key}
              onClick={() => setActiveSection(key)}
              size="small"
              sx={{
                flex: 1,
                backgroundColor: isActive
                  ? alpha(theme.palette.primary.main, 0.12)
                  : theme.palette.common.white,
                color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                border: `1px solid ${
                  isActive ? theme.palette.primary.main : theme.palette.divider
                }`,
                fontWeight: isActive ? 700 : 500,
                '&:hover': {
                  backgroundColor: isActive
                    ? alpha(theme.palette.primary.main, 0.18)
                    : theme.palette.grey[100],
                },
                whiteSpace: 'nowrap',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>{label}</span>
                {hasExcluded && (
                  <WarningAmberIcon
                    sx={{ fontSize: '0.95rem', color: theme.palette.warning.main }}
                  />
                )}
              </Stack>
            </Button>
          );
        })}
      </Box>

      {/* Section content */}
      <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
        {activeSection === 'alarmRules' && (
          <AlarmRulesSection template={template} tagMapping={tagMapping} />
        )}
        {activeSection === 'assets' && (
          <AssetsSection template={template} tagMapping={tagMapping} />
        )}
        {activeSection === 'controllers' && <ControllersSection template={template} />}
        {activeSection === 'widgets' && (
          <WidgetsSection template={template} tagMapping={tagMapping} />
        )}
        {activeSection === 'sitePreview' && (
          <SitePreviewSection template={template} tagMapping={tagMapping} />
        )}
      </Box>
    </Stack>
  );
};
